/**
 * IntakeSourcesPanel — ops-menu-reorg (AC-OPSNAV-2)
 *
 * Panel extraction of IntakeSourcesPage body for embedding inside the
 * Structure Settings tabbed page.  Renders the full Intake Sources surface
 * (scan-routine banner, status filter, sources table / empty state, New CTA)
 * WITHOUT a nested RorosPage wrapper — following the same pattern as
 * OperatingModePanel, DomainsPanel, CadencePanel, ConstitutionPanel, and
 * DecisionMakingPanel.
 *
 * IntakeSourcesPage re-uses this panel so both entry-points render
 * identical content.
 *
 * Writing (create / edit / disable) remains gated server-side by
 * ROROS_ARTIFACTS_EDIT (in intake.routes.ts) — no client-side change.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Plus, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { t, __resolve } from '@/lib/i18n';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { showToast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useRorosIntakeSources,
  useRorosIntakeScanRoutine,
  useRorosIntakeSourceDisable,
} from '@/hooks/roros/intake';
import { formatDate } from '@/lib/localeFormatters';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { IntakeSourceFormDialog } from './IntakeSourceFormDialog';
import type { RorosIntakeSourceDTO } from '@/services/roros/intake.service';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusBadge(status: string): React.ReactNode {
  if (status === 'active') {
    return (
      <Badge variant="success" data-testid="intake-source-status-active">
        {t('Active')}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" data-testid="intake-source-status-archived">
      {t('Archived')}
    </Badge>
  );
}

function connectorBadge(connectorType: string): React.ReactNode {
  const labels: Record<string, string> = {
    manual: t('Manual'),
    webhook: t('Webhook'),
    email: t('Email'),
    api: t('API'),
  };
  return (
    <Badge variant="secondary" className="text-xs">
      {labels[connectorType] ?? connectorType}
    </Badge>
  );
}

function humanRelTime(iso: string | null | undefined): string {
  if (!iso) return t('Never run');
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600_000);
  if (hours < 1) return t('Less than 1 hour ago');
  if (hours < 24) return __resolve('ui::{{count}} hours ago', { count: hours });
  const days = Math.floor(hours / 24);
  return __resolve('ui::{{count}} days ago', { count: days });
}

function humanFutureTime(iso: string | null | undefined): string {
  if (!iso) return t('Status unknown');
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return t('Overdue');
  const hours = Math.floor(diff / 3600_000);
  if (hours < 1) return t('In less than 1 hour');
  if (hours < 24) return __resolve('ui::In {{count}} hours', { count: hours });
  const days = Math.floor(hours / 24);
  return __resolve('ui::In {{count}} days', { count: days });
}

// ─── Scan Routine Banner ──────────────────────────────────────────────────────

function ScanRoutineBanner() {
  const { data: routine, isLoading, isError } = useRorosIntakeScanRoutine();

  if (isLoading) {
    return (
      <div
        className="mb-4 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground flex items-center gap-2"
        data-testid="scan-routine-banner-loading"
      >
        <Clock className="h-4 w-4 shrink-0" aria-hidden />
        {t('Loading Intake Scan status…')}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
        data-testid="scan-routine-banner-error"
        role="alert"
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
        {t('Failed to load Intake Scan status. Check service health.')}
      </div>
    );
  }

  if (routine === null || routine === undefined) {
    return (
      <div
        className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center gap-2"
        data-testid="scan-routine-banner-not-configured"
        role="status"
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
        {t('No Intake Scan routine configured for this org.')}
      </div>
    );
  }

  return (
    <div
      className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex flex-wrap items-center gap-x-4 gap-y-1"
      data-testid="scan-routine-banner"
      role="status"
    >
      <span className="flex items-center gap-1.5">
        <CheckCircle className="h-4 w-4 shrink-0" aria-hidden />
        <strong>{t('Intake Scan')}</strong>
        <Badge className="bg-blue-100 text-blue-700 text-xs">{routine.status}</Badge>
      </span>
      <span>
        {t('Last scan:')} <strong>{humanRelTime(routine.lastRunAt)}</strong>
      </span>
      <span>
        {t('Next scan:')} <strong>{humanFutureTime(routine.nextRunAt)}</strong>
      </span>
      <span className="text-blue-600">
        {routine.runCount} {t('total runs')}
      </span>
    </div>
  );
}

// ─── Panel (no RorosPage wrapper) ─────────────────────────────────────────────

/**
 * IntakeSourcesPanel — the full Intake Sources management surface, suitable
 * for embedding inside a Tabs > TabsContent block.
 *
 * Renders page header (title + New CTA), scan-routine banner, status filter,
 * and the sources table / empty state.  All data-testids are preserved so the
 * existing E2E test suite continues to pass against the tab location.
 */
export function IntakeSourcesPanel() {
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editSource, setEditSource] = useState<RorosIntakeSourceDTO | null>(null);
  const [disableTarget, setDisableTarget] = useState<RorosIntakeSourceDTO | null>(null);

  const params = useMemo(
    () => ({ status: statusFilter }),
    [statusFilter],
  );

  const sourcesQuery = useRorosIntakeSources(params);
  const disableMutation = useRorosIntakeSourceDisable();

  // ─── URL routing: ?edit-intake=<id> opens the edit sheet ─────────────────
  const getSourceId = useCallback((s: RorosIntakeSourceDTO) => s.id, []);
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: 'edit-intake',
    items: sourcesQuery.data as RorosIntakeSourceDTO[] | undefined,
    getId: getSourceId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditSource(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  const handleDisableConfirm = () => {
    if (!disableTarget) return;
    disableMutation.mutate(disableTarget.id, {
      onSuccess: () => {
        showToast.success(t('Intake source disabled'));
        setDisableTarget(null);
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to disable intake source'));
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Panel header — inline like other Structure Settings panels */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-muted-foreground" aria-hidden />
          <h3 className="text-base font-semibold text-foreground">{t('Intake Sources')}</h3>
        </div>
        <Button
          onClick={() => { setEditSource(null); setIsFormOpen(true); }}
          leftIcon={<Plus className="h-4 w-4" />}
          data-testid="intake-sources-new-button"
        >
          {t('New Intake Source')}
        </Button>
      </div>

      <ScanRoutineBanner />

      {/* Status filter */}
      <div className="mb-4" data-testid="intake-sources-status-filter">
        <Select
          label={t('Status')}
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          options={[
            { value: 'active', label: t('Active') },
            { value: 'archived', label: t('Archived') },
            { value: 'all', label: t('All') },
          ]}
        />
      </div>

      <QueryPage
        query={sourcesQuery}
        emptyMessage={t('No intake sources found. Create one to define a feed for the daily Intake Scan.')}
        emptyIcon={<Inbox className="h-8 w-8" />}
        emptyAction={
          <Button
            onClick={() => { setEditSource(null); setIsFormOpen(true); }}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            {t('Create your first intake source')}
          </Button>
        }
      >
        {(sources) => {
          const list = sources as RorosIntakeSourceDTO[];
          if (list.length === 0) {
            return (
              <div className="text-center text-sm text-muted-foreground py-8">
                {t('No intake sources match the current filter.')}
              </div>
            );
          }

          return (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table
                className="w-full min-w-[600px] text-sm"
                aria-label={t('Intake Sources')}
                data-testid="intake-sources-table"
              >
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">
                      {t('Title')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-48">
                      {t('Description')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">
                      {t('Connector')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-24">
                      {t('Status')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-32">
                      {t('Updated')}
                    </th>
                    <th className="text-right text-xs font-semibold text-muted-foreground px-4 py-2.5 w-36">
                      {t('Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((src) => (
                    <tr
                      key={src.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                      data-testid="intake-source-row"
                    >
                      <td className="px-4 py-3 font-medium text-foreground">{src.title}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs max-w-[200px]">
                        <span className="line-clamp-2">{src.description ?? t('—')}</span>
                      </td>
                      <td className="px-4 py-3">{connectorBadge(src.connectorType)}</td>
                      <td className="px-4 py-3">{statusBadge(src.status)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(new Date(src.updatedAt))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditSource(src); setIsFormOpen(true); }}
                            data-testid="intake-source-edit-button"
                          >
                            {t('Edit')}
                          </Button>
                          {src.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDisableTarget(src)}
                              data-testid="intake-source-disable-button"
                            >
                              {t('Disable')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
      </QueryPage>

      {/* Create / Edit form dialog */}
      <IntakeSourceFormDialog
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditSource(null); }}
        onSaved={() => sourcesQuery.refetch()}
        editSource={editSource}
        urlKey="edit-intake"
      />

      {/* Disable confirm dialog */}
      <ConfirmDialog
        isOpen={!!disableTarget}
        onClose={() => setDisableTarget(null)}
        onConfirm={handleDisableConfirm}
        title={t('Disable this intake source?')}
        description={t(
          'Disabling archives the intake source. The Intake Scan routine will no longer include it. ' +
          'This action is recorded in the audit log (GOV-05).',
        )}
        variant="destructive"
        confirmText={t('Disable')}
        isLoading={disableMutation.isPending}
      />
    </div>
  );
}
